<template>
  <div>
    <v-menu v-model="menu" min-width="auto" transition="scale-transition" :close-on-content-click="false">
      <template #activator="{ props }">
        <AGTextField
          :id="id"
          append-icon="mdi-calendar-outline"
          autocomplete="off"
          class="datepicker-input"
          density="compact"
          hide-details="auto"
          clearable
          readonly
          :disabled="disabled"
          :label="label"
          :model-value="dateText"
          :placeholder="placeholder"
          :rules="rules"
          :message-color="messageColor"
          :hint="hint"
          v-bind="props"
          @click:clear="onClear"
        />
      </template>
      <v-date-picker
        multiple="range"
        hide-header
        :allowed-dates="allowedDates ? allowedDates : null"
        :max="maxPeriod"
        :min="minPeriod"
        :model-value="convertedDates"
        @update:model-value="onDatePick"
      >
        <template #actions>
          <v-spacer />
          <AGButton type="secondary" @click="onCancel"> Cancelar </AGButton>
          <AGButton type="primary" @click="onConfirm"> Confirmar </AGButton>
        </template>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import AGButton from '@/components/common/AGButton.vue';
import AGTextField from '@/components/common/AGTextField.vue';
import { MESSAGE_COLOR } from '@/constants/ConstantMessageColor';
import UtilsDate from '@/utils/UtilsDate';
import { cloneDeep, isEmpty } from 'lodash';

export default {
  components: { AGTextField, AGButton },

  emits: ['change', 'update:modelValue'],

  props: {
    modelValue: {
      type: Array,
      default: () => {
        return [];
      },
    },

    allowedDates: {
      type: Function,
      default: null,
    },

    id: {
      type: String,
      required: true,
    },

    label: {
      type: String,
      require: true,
    },

    hint: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: 'Selecione uma ou mais datas',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    max: {
      type: Number,
      default: undefined,
    },

    min: {
      type: Number,
      default: undefined,
    },

    maxType: {
      type: String,
      default: 'days',
    },

    rules: {
      type: Array,
      default: () => [],
    },

    messageColor: {
      type: String,
      default: MESSAGE_COLOR.DEFAULT,
      validator: (val) => Object.values(MESSAGE_COLOR).includes(val),
    },
  },

  data() {
    return {
      menu: false,
      localDates: [],
    };
  },

  computed: {
    dateText() {
      if (isEmpty(this.modelValue)) return null;

      const dates = [this.modelValue.at(0)];

      if (this.modelValue.length > 1) dates.push(this.modelValue.at(-1));

      return dates.map((date) => UtilsDate.format(date)).join(' - ');
    },
    convertedDates() {
      const [firstDate, lastDate] = this.localDates;
      return UtilsDate.generateDates(firstDate, lastDate ?? firstDate);
    },
    minPeriod() {
      if (!this.min) return;

      const period = this.localDates[1] ?? this.localDates[0];
      if (!period) return;

      return UtilsDate.subtract(period, this.min, this.maxType);
    },

    maxPeriod() {
      if (!this.max) return;
      if (!this.localDates[0]) return;

      return UtilsDate.add(this.localDates[0], this.max, this.maxType);
    },
  },

  watch: {
    modelValue: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.localDates = [];
        } else {
          this.localDates = val;
        }
      },
    },
  },

  methods: {
    onDatePick(val) {
      if (isEmpty(val)) {
        this.localDates = [];
        return;
      }

      const firstDate = val[0];
      const lastDate = val[val.length - 1] ?? firstDate;
      this.localDates = [UtilsDate.format(firstDate, 'Y-MM-DD'), UtilsDate.format(lastDate, 'Y-MM-DD')];
    },

    onCancel() {
      this.menu = false;
      this.localDates = cloneDeep(this.modelValue);
      this.$emit('update:modelValue', this.modelValue || []);
    },

    onConfirm() {
      this.menu = false;
      this.$emit('update:modelValue', this.localDates);
    },

    onClear() {
      this.localDates = [];
      this.$emit('update:modelValue', []);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/datepicker.scss';
</style>
